import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { rgba } from 'polished';
import React, { useContext, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { typography } from '../../styles/typography';
import { FormContext } from '../../validation/Form';
import { ErrorLabel, Label as InputLabel } from './Label';

export type InputSize = 'small' | 'medium' | 'big';
export type InputVariant = 'fullBorder' | 'bottomBorder' | 'noBorder';
export type LabelVariant = 'aboveInput' | 'insideInput';
export type TextAlign = 'right' | 'left' | 'center';
export type ErrorMessageVariant = 'belowInput' | 'insideInput';

export const InputHost = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  & > * + * {
    margin-top: 8px;
  }
`;

export interface BaseInputProps {
  hackSuffix?: string;
  isValid?: boolean;
  errorMessage?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  inputSize?: InputSize;
  label?: string;
  variant?: InputVariant;
  visited?: boolean;
  labelVariant?: LabelVariant;
  textAlign?: TextAlign;
  errorMessageVariant?: ErrorMessageVariant;
}

export interface InputProps
  extends React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    BaseInputProps {}

export const inputCss = ({
  inputSize,
  variant,
  theme,
  isValid,
  value,
  hackSuffix,
  visited,
  labelVariant,
}: BaseInputProps & { value?: any; theme?: Theme }) => css`
  height: ${inputSize === 'small' ? '40px' : '64px'};
  padding: ${inputSize === 'small'
    ? '8px'
    : inputSize === 'big'
    ? '19px 20px'
    : '28px 20px 12px 15px'};
  color: ${theme?.shades.primary5};

  ${labelVariant === 'aboveInput' && 'padding: 20px 16px'};

  ${variant === 'fullBorder' &&
  `box-shadow: 0 0 0 1px
     ${rgba(theme?.colors.foreground ?? '', 0.7)}`};

  ${variant === 'bottomBorder' &&
  `border-bottom: 1px solid
     ${rgba(theme?.colors.foreground ?? '', 0.7)}`};

  &:hover {
    box-shadow: 0 0 0 1px ${theme?.colors.foreground};

    ${variant === 'fullBorder' &&
    `box-shadow: 0 0 0 1px
     ${theme?.colors.foreground}`};

    ${variant === 'bottomBorder' &&
    `border-bottom: 1px solid
     ${rgba(theme?.colors.foreground ?? '', 0.3)}`};

    &::placeholder {
      color: ${theme?.shades.primary5};
    }

    & + label {
      color: ${theme?.shades.primary5};
    }
  }

  &:active,
  &:focus {
    box-shadow: 0 0 0 2px ${theme?.shades.primary3};
  }

  ${isValid === false &&
  `
    box-shadow: 0 0 0 2px ${theme?.colors.secondary};
  `}

  &::placeholder {
    color: ${rgba(theme?.shades.primary5 ?? '', 0.6)};
  }

  &:disabled {
    opacity: 0.5;
    background-color: ${rgba(theme?.colors.foreground ?? '', 0.05)};
  }

  transition: box-shadow 0.1s ease-in;
`;

const BaseInput = styled.input`
  ${inputCss};
  ${(props) =>
    props.inputSize === 'small'
      ? typography.numbers
      : props.inputSize === 'big'
      ? typography.input2
      : typography.body};
`;

export const InsideLabel = styled.label<{
  isActive: boolean;
  isValid?: boolean;
}>`
  position: absolute;
  ${(props) => (props.isActive ? typography.label : typography.numbers2)};
  top: ${(props) => (props.isActive ? '4px' : 'calc(50% - 12px)')};
  left: 15px;
  opacity: ${(props) => (props.isActive && props.isValid ? '65%' : '100%')};
  transition: all 200ms ease-in;
  margin-top: 0px;
  color: ${({ isValid, theme }) =>
    rgba(theme?.shades.primary5 ?? '', isValid !== undefined ? 1 : 0.6)};
`;

export function Input({
  inputRef,
  isValid,
  label,
  errorMessage,
  variant = 'fullBorder',
  labelVariant = 'insideInput',
  textAlign = 'left',
  errorMessageVariant = 'belowInput',
  ...rest
}: InputProps) {
  const formContext = useContext(FormContext);
  const [visited, setVisited] = useState(false);
  const [focused, setFocused] = useState(false);
  const [uuid] = useState(v4());
  const [errorLabelId] = useState(v4());
  const showValidState = useMemo(() => {
    return (
      (!focused && formContext.validate && !isValid) ||
      (!focused && visited && !!rest.value && rest.value !== rest.hackSuffix)
    );
  }, [
    visited,
    isValid,
    focused,
    rest.value,
    rest.hackSuffix,
    formContext.validate,
  ]);

  const labelOrError = useMemo(() => {
    if (errorMessageVariant !== 'insideInput') {
      return label;
    }
    if (isValid === false && errorMessage) {
      return errorMessage;
    }
    return label;
  }, [label, errorMessageVariant, errorMessage, isValid]);

  return (
    <InputHost>
      {label && labelVariant === 'aboveInput' && (
        <InputLabel
          size={rest.inputSize}
          htmlFor={rest.id ?? uuid}
          disabled={rest.disabled}
          isValid={showValidState ? isValid : undefined}
        >
          {label}
        </InputLabel>
      )}
      <BaseInput
        ref={inputRef}
        id={rest.id ?? uuid}
        isValid={showValidState ? isValid : undefined}
        aria-invalid={!isValid}
        visited={visited}
        aria-describedby={errorLabelId}
        variant={variant}
        labelVariant={labelVariant}
        style={{ textAlign }}
        {...rest}
        onFocus={(e) => {
          setFocused(true);
          if (rest.onFocus) {
            rest.onFocus(e);
          }
        }}
        onBlur={(e) => {
          setVisited(true);
          setFocused(false);
          if (rest.onBlur) {
            rest.onBlur(e);
          }
        }}
      />
      {labelVariant === 'insideInput' && labelOrError && (
        <InsideLabel
          htmlFor={rest.id ?? uuid}
          isActive={
            focused ||
            (rest.value !== '' &&
              rest.value !== undefined &&
              rest.value !== rest.hackSuffix)
          }
          isValid={showValidState ? isValid : undefined}
        >
          {labelOrError}
        </InsideLabel>
      )}
      {errorMessage && errorMessageVariant === 'belowInput' && (
        <ErrorLabel id={errorLabelId} size={rest.inputSize}>
          {errorMessage}
        </ErrorLabel>
      )}
    </InputHost>
  );
}
